/*
 * ===== PALAS: ANALYTICS SCHEMA + HELPERS =====
 *
 * Typage et helpers partagés pour tous les events analytics (cart + checkout)
 * envoyés à PostHog et Klaviyo.
 *
 * Règle unique : les events respectent la forme AnalyticsEvent ci-dessous.
 *   - Les infos cart sont TOUJOURS dans la clé `cart` (jamais à la racine).
 *   - Les infos checkout sont TOUJOURS dans la clé `checkout`.
 *   - Seules les propriétés spécifiques à l'event vivent à la racine
 *     (source, changed_items, discount_code, discount_amount, ...).
 *
 * Klaviyo reçoit une version à plat (convention commerce Klaviyo) via
 * `capture(..., klaviyoEventName)`. PostHog reçoit la forme canonique.
 *
 * Le pixel checkout (sections/pixel sandboxé) ne peut pas importer ce fichier ;
 * il duplique la forme et documente le lien via son propre bloc de typedef.
 */

/**
 * @typedef {Object} CartItem
 * @property {string} id            Variant ID (string).
 * @property {string} product_id
 * @property {string} sku
 * @property {string} title
 * @property {string} vendor
 * @property {string} product_type
 * @property {string} variant_title
 * @property {number} quantity
 * @property {number} price         Unit price (currency units, not cents).
 * @property {number} original_price
 * @property {number} line_price
 * @property {number} total_discount
 * @property {Array<{title: string, amount: number}>} discounts
 * @property {string} image_url
 * @property {string} url
 */

/**
 * @typedef {Object} CartPayload
 * @property {string|null} token
 * @property {number} total_items
 * @property {number} total_price
 * @property {number} subtotal_price
 * @property {number} total_discount
 * @property {string} currency
 * @property {string} url
 * @property {Array<{title: string, amount: number}>} cart_level_discounts
 * @property {Array<CartItem>} items
 */

/**
 * @typedef {Object} CheckoutItem
 * @property {string} id
 * @property {string} product_id
 * @property {string} title
 * @property {number} quantity
 * @property {number} price
 */

/**
 * @typedef {Object} CheckoutDiscount
 * @property {string} title
 * @property {string} type
 * @property {string|number|null} value
 * @property {string} allocation_method
 * @property {string} target_selection
 * @property {string} target_type
 */

/**
 * @typedef {Object} CheckoutPayload
 * @property {string} token
 * @property {number|null} total_price
 * @property {number|null} subtotal_price
 * @property {number|null} total_tax
 * @property {string} currency
 * @property {string|null} email
 * @property {string|null} phone
 * @property {string|null} shipping_method
 * @property {number|null} shipping_price
 * @property {number|null} discounts_amount
 * @property {Array<CheckoutDiscount>} discounts
 * @property {string|null} shopify_order_id
 * @property {string|null} shopify_customer_id
 * @property {boolean|null} is_first_order
 * @property {Array<CheckoutItem>} items
 */

/**
 * Event envelope envoyé à PostHog. Les clés non pertinentes pour l'event
 * sont omises (pas de valeurs nulles parasites).
 *
 * @typedef {Object} AnalyticsEvent
 * @property {string} [source]                'drawer' | 'page' | 'url' | 'cart' | ...
 * @property {CartPayload} [cart]
 * @property {CheckoutPayload} [checkout]
 * @property {Array<CartItem & {quantity_change: number}>} [changed_items]
 * @property {string} [discount_code]
 * @property {number} [discount_amount]
 */

(function(window) {
  var PalasAnalytics = {};
  var MUID_STORAGE_KEY = 'palas_muid';
  var TRACKING_CONTEXT_STORAGE_KEY = 'palas_tracking_context';
  var CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid'];
  var TRACKING_PARAM_KEYS = CLICK_ID_KEYS.concat([
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'utm_id',
    'utm_source_platform',
    'utm_creative_format',
    'utm_marketing_tactic',
    'campaign_id',
    'campaign_name',
    'ad_group_id',
    'ad_group_name',
    'adgroup_id',
    'ad_id',
    'ad_name',
    'adset_id',
    'adset_name',
    'creative_id',
    'placement',
    'network',
    'matchtype'
  ]);

  function randomMuid() {
    var bytes = null;
    try {
      if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
        bytes = new Uint8Array(16);
        window.crypto.getRandomValues(bytes);
      }
    } catch(e) {}
    if (!bytes) {
      bytes = [];
      for (var i = 0; i < 16; i += 1) bytes.push(Math.floor(Math.random() * 256));
    }
    var hex = '';
    for (var j = 0; j < bytes.length; j += 1) {
      hex += ('0' + bytes[j].toString(16)).slice(-2);
    }
    return 'muid_' + hex;
  }

  function validMuid(value) {
    value = typeof value === 'string' ? value.trim() : '';
    return /^muid_[a-f0-9]{32}$/i.test(value) ? value.toLowerCase() : null;
  }

  function muidFromPostHogDistinctId(distinctId) {
    distinctId = typeof distinctId === 'string' ? distinctId.trim().toLowerCase() : '';
    var hex = distinctId.replace(/[^a-f0-9]/g, '');
    return hex.length >= 32 ? 'muid_' + hex.slice(0, 32) : null;
  }

  /**
   * Build a CartPayload from a raw /cart.js response.
   * @param {object} cartJson
   * @returns {CartPayload|null}
   */
  PalasAnalytics.buildCartPayload = function(cartJson) {
    if (!cartJson) return null;
    var origin = window.location.origin;
    return {
      token: cartJson.token || null,
      total_items: cartJson.item_count || (cartJson.items || []).reduce(function(total, item) {
        return total + parseInt(item.quantity || 0, 10);
      }, 0),
      total_price: parseFloat((cartJson.total_price || 0) / 100),
      subtotal_price: parseFloat((cartJson.items_subtotal_price || cartJson.total_price || 0) / 100),
      total_discount: parseFloat((cartJson.total_discount || 0) / 100),
      currency: cartJson.currency,
      url: origin + '/cart',
      cart_level_discounts: (cartJson.cart_level_discount_applications || []).map(function(d) {
        return { title: d.title, amount: parseFloat(d.total_allocated_amount / 100) };
      }),
      items: (cartJson.items || []).map(function(item) {
        return {
          id: item.variant_id.toString(),
          product_id: item.product_id.toString(),
          sku: item.sku,
          title: item.title,
          vendor: item.vendor,
          product_type: item.product_type,
          variant_title: item.variant_title,
          quantity: item.quantity,
          price: parseFloat(item.price / 100),
          final_price: parseFloat((item.final_price == null ? item.price : item.final_price) / 100),
          properties: item.properties || {},
          original_price: parseFloat(item.original_price / 100),
          line_price: parseFloat(item.line_price / 100),
          final_line_price: parseFloat((item.final_line_price == null ? item.line_price : item.final_line_price) / 100),
          total_discount: parseFloat(item.total_discount / 100),
          discounts: (item.discounts || []).map(function(d) {
            return { title: d.title, amount: parseFloat(d.amount / 100) };
          }),
          image_url: item.image,
          url: origin + item.url
        };
      })
    };
  };

  /** Independent complete resource evidence; delta ecommerce.items stays unchanged. */
  PalasAnalytics.buildCommerceSnapshot = function(cart, revision) {
    if (!cart || !cart.token || !/^[A-Z]{3}$/.test(cart.currency) || !Array.isArray(cart.items)) return null;
    function minor(value) { return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? Math.round(value * 100) : NaN; }
    function http(value) { return typeof value === 'string' && /^https?:\/\//.test(value); }
    var lines = cart.items.map(function(item) {
      return {
        variant_id: String(item.id || ''), title: item.title, quantity: item.quantity,
        unit_price_minor: minor(item.final_price == null ? item.price : item.final_price),
        line_total_minor: minor(item.final_line_price == null ? item.line_price : item.final_line_price),
        image_url: item.image_url && item.image_url.indexOf('//') === 0 ? 'https:' + item.image_url : item.image_url,
        product_url: item.url, sku: item.sku, variant_title: item.variant_title
      };
    });
    if (!http(cart.url) || !Number.isSafeInteger(minor(cart.total_price)) || lines.some(function(line) {
      return !line.variant_id || !line.title || !Number.isSafeInteger(line.quantity) || line.quantity < 1 ||
        !Number.isSafeInteger(line.unit_price_minor) || !Number.isSafeInteger(line.line_total_minor) || !http(line.image_url) || !http(line.product_url);
    })) return null;
    var snapshot = { cart_id: String(cart.token), revision: revision, completeness: 'complete', resource_type: 'cart',
      currency: cart.currency, value_minor: minor(cart.total_price),
      recovery_url: lines.length ? cart.url.replace(/\/$/, '') + '/' + lines.map(function(line) { return encodeURIComponent(line.variant_id) + ':' + line.quantity; }).join(',') : cart.url, lines: lines };
    if (Number.isSafeInteger(minor(cart.subtotal_price))) snapshot.subtotal_minor = minor(cart.subtotal_price);
    if (Number.isSafeInteger(minor(cart.total_discount))) snapshot.discount_minor = minor(cart.total_discount);
    if (window.document && window.document.documentElement && window.document.documentElement.lang) snapshot.locale = window.document.documentElement.lang;
    return snapshot;
  };

  /**
   * Fetch /cart.js and return a CartPayload. Resolves to null on error.
   * @returns {Promise<CartPayload|null>}
   */
  PalasAnalytics.fetchCartPayload = function() {
    return fetch(window.location.origin + '/cart.js', { credentials: 'same-origin' })
      .then(function(res) { return res.json(); })
      .then(function(data) { return PalasAnalytics.buildCartPayload(data); })
      .catch(function() { return null; });
  };

  PalasAnalytics.eventId = function(eventName) {
    var randomPart = Math.random().toString(36).slice(2, 10);
    return [
      'palas',
      eventName,
      Date.now().toString(36),
      randomPart
    ].join('_');
  };

  PalasAnalytics.trackingUser = function(extra) {
    var user = Object.assign({}, extra || {});
    var tracking = PalasAnalytics.trackingContext();
    CLICK_ID_KEYS.forEach(function(key) {
      if (tracking[key] && !user[key]) user[key] = tracking[key];
    });
    return user;
  };

  PalasAnalytics.trackingContext = function() {
    var stored = {};
    var changed = false;
    try {
      stored = JSON.parse(window.localStorage.getItem(TRACKING_CONTEXT_STORAGE_KEY) || '{}') || {};
    } catch(e) {
      stored = {};
    }

    try {
      var params = new URLSearchParams(window.location.search || '');
      TRACKING_PARAM_KEYS.forEach(function(key) {
        var value = params.get(key);
        if (value) {
          stored[key] = value;
          changed = true;
        }
      });
    } catch(e) {}

    var output = {};
    TRACKING_PARAM_KEYS.forEach(function(key) {
      if (stored[key]) output[key] = stored[key];
    });

    if (changed && Object.keys(output).length > 0) {
      try {
        window.localStorage.setItem(
          TRACKING_CONTEXT_STORAGE_KEY,
          JSON.stringify(Object.assign({}, output, { updated_at: new Date().toISOString() }))
        );
      } catch(e) {}
    }

    return output;
  };

  PalasAnalytics.withTrackingProperties = function(payload) {
    payload = payload || {};
    var tracking = PalasAnalytics.trackingContext();
    TRACKING_PARAM_KEYS.forEach(function(key) {
      if (tracking[key] && !payload[key]) payload[key] = tracking[key];
    });
    CLICK_ID_KEYS.forEach(function(key) {
      if (!tracking[key]) return;
      payload.user = Object.assign({}, payload.user || {});
      payload.user_data = Object.assign({}, payload.user_data || {});
      if (!payload.user[key]) payload.user[key] = tracking[key];
      if (!payload.user_data[key]) payload.user_data[key] = tracking[key];
    });
    return payload;
  };

  // Read identifiers already created by the destination, without loading a second tracker.
  PalasAnalytics.withBrowserIdentifiers = function(payload) {
    payload = payload || {};
    var consent = payload.consent || PalasAnalytics.consent();
    // Re-check queued captures: revocation must remove previously collected IDs.
    if (consent.analytics_storage !== true) {
      delete payload.ga_client_id;
      delete payload.ga_session_id;
      delete payload.ga_session_ids;
    }
    if (consent.ad_storage !== true || consent.ad_user_data !== true || consent.ad_personalization !== true) {
      delete payload.fbp;
      delete payload.fbc;
      delete payload.epik;
    }
    var sessions = {};
    String(document.cookie || '').split(';').forEach(function(part) {
      var index = part.indexOf('=');
      if (index < 0) return;
      var name = part.slice(0, index).trim(), value;
      try { value = decodeURIComponent(part.slice(index + 1).trim()); } catch (_) { return; }
      if (consent.analytics_storage === true) {
        if (name === '_ga') {
          var client = value.match(/^GA\d+\.\d+\.(\d+\.\d+)$/);
          if (client) payload.ga_client_id = payload.ga_client_id || client[1];
        } else if (name.indexOf('_ga_') === 0) {
          var session = value.match(/^GS1\.\d+\.(\d+)\./) || value.match(/^GS2\.\d+\.s(\d+)(?:\$|$)/);
          if (session) sessions['G-' + name.slice(4)] = session[1];
        }
      }
      if (consent.ad_storage === true && consent.ad_user_data === true && consent.ad_personalization === true) {
        if (name === '_fbp' || name === '_fbc') payload[name.slice(1)] = payload[name.slice(1)] || value;
        if (name === '_epik') payload.epik = payload.epik || value;
      }
    });
    if (Object.keys(sessions).length) payload.ga_session_ids = payload.ga_session_ids || sessions;
    return payload;
  };

  PalasAnalytics.withConsentProperties = function(payload) {
    payload = payload || {};
    if (PalasAnalytics.signalIdentity) Object.keys(PalasAnalytics.signalIdentity).forEach(function(key) { if (payload[key] === undefined) payload[key]=PalasAnalytics.signalIdentity[key]; });
    if (!payload.palas_signal_document_id && window.PalasSignalProbe) payload.palas_signal_document_id=window.PalasSignalProbe.id;
    var consent = payload.consent || PalasAnalytics.consent();
    payload.consent = consent;
    payload.palas_consent_captured = true;
    payload.analytics_storage = consent.analytics_storage === true;
    payload.ad_storage = consent.ad_storage === true;
    payload.ad_user_data = consent.ad_user_data === true;
    payload.ad_personalization = consent.ad_personalization === true;
    payload.palas_consent_analytics = consent.analytics_storage === true;
    payload.palas_consent_ads = consent.ad_storage === true && consent.ad_user_data === true && consent.ad_personalization === true;
    payload.palas_consent_source = consent.source || 'unknown';
    return payload;
  };

  PalasAnalytics.setMuid = function(muid) {
    muid = validMuid(muid);
    if (!muid) return null;
    PalasAnalytics.muid = muid;
    window.__palasMuid = muid;
    try { window.localStorage.setItem(MUID_STORAGE_KEY, muid); } catch(e) {}
    try {
      if (window.posthog && typeof window.posthog.register === 'function') {
        window.posthog.register({ muid: muid, palas_muid: muid });
      }
    } catch(e) {}
    return muid;
  };

  PalasAnalytics.setMuidFromPostHogDistinctId = function(distinctId) {
    return PalasAnalytics.getMuid({ create: false }) || PalasAnalytics.setMuid(muidFromPostHogDistinctId(distinctId));
  };

  PalasAnalytics.getMuid = function(options) {
    var create = !options || options.create !== false;
    var current = validMuid(PalasAnalytics.muid) || validMuid(window.__palasMuid);
    if (!current) {
      try { current = validMuid(window.localStorage.getItem(MUID_STORAGE_KEY)); } catch(e) {}
    }
    if (!current && window.posthog && typeof window.posthog.get_distinct_id === 'function') {
      try { current = muidFromPostHogDistinctId(window.posthog.get_distinct_id()); } catch(e) {}
    }
    if (!current && create) current = randomMuid();
    return current ? PalasAnalytics.setMuid(current) : null;
  };

  PalasAnalytics.withMuidProperties = function(payload) {
    payload = payload || {};
    var muid = PalasAnalytics.getMuid({ create: true });
    if (!muid) return payload;
    payload.muid = payload.muid || muid;
    payload.palas_muid = payload.palas_muid || muid;
    payload.user = Object.assign({}, payload.user || {}, { muid: payload.user && payload.user.muid ? payload.user.muid : muid });
    payload.user_data = Object.assign({}, payload.user_data || {}, {
      muid: payload.user_data && payload.user_data.muid ? payload.user_data.muid : muid
    });
    return payload;
  };

  PalasAnalytics.capturePostHog = function(eventName, payload) {
    if (eventName === 'page_view') return;
    payload = PalasAnalytics.withTrackingProperties(payload || {});
    payload = PalasAnalytics.withConsentProperties(payload || {});
    payload = PalasAnalytics.withMuidProperties(payload);
    payload = PalasAnalytics.withBrowserIdentifiers(payload);
    if (window.posthog && typeof window.posthog.capture === 'function') {
      window.posthog.capture(eventName, payload);
      return;
    }
    window.__palasPostHogQueue = window.__palasPostHogQueue || [];
    window.__palasPostHogQueue.push({ event: eventName, properties: payload });
  };

  PalasAnalytics.pushEventHub = function(eventName, payload) {
    payload = payload || {};
    var eventId = payload.event_id || (payload.event_data && payload.event_data.id) || PalasAnalytics.eventId(eventName);
    payload.event_id = eventId;
    payload.event_data = Object.assign({
      id: eventId,
      timestamp: new Date().toISOString(),
      source: 'shopify_theme'
    }, payload.event_data || {});
    PalasAnalytics.capturePostHog(eventName, payload);
  };

  PalasAnalytics.eventHubEndpoint = 'https://admin.fancypalas.com/api/event-hub';

  PalasAnalytics.toEventHubName = function(eventName) {
    var map = {
      'cart:product_added': 'add_to_cart',
      'cart:product_removed': 'remove_from_cart',
      'cart:viewed': 'view_cart',
      'cart:updated': null,
      'cart:cleared': null,
      'cart:closed': null,
      'cart:discount_applied': null
    };
    return Object.prototype.hasOwnProperty.call(map, eventName) ? map[eventName] : eventName;
  };

  PalasAnalytics.context = function() {
    var pageType = 'other';
    if (document.body) {
      if (document.body.classList.contains('template-index')) pageType = 'home';
      else if (document.body.classList.contains('template-collection')) pageType = 'collection';
      else if (document.body.classList.contains('template-product')) pageType = 'product';
      else if (document.body.classList.contains('template-search')) pageType = 'search';
      else if (document.body.classList.contains('template-cart')) pageType = 'cart';
    }
    return {
      url: window.location.href,
      referrer: document.referrer || '',
      page_type: pageType,
      market: (window.Shopify && window.Shopify.country) || null,
      locale: (document.documentElement && document.documentElement.lang) || null
    };
  };

  PalasAnalytics.consent = function() {
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (!cp) {
      return {
        analytics_storage: false,
        ad_storage: false,
        ad_user_data: false,
        ad_personalization: false,
        state: 'unknown',
        source: 'shopify_customer_privacy_unavailable'
      };
    }

    var analytics = false;
    var marketing = false;
    var saleOfData = true;
    try {
      analytics = typeof cp.analyticsProcessingAllowed === 'function' ? cp.analyticsProcessingAllowed() === true : false;
      marketing = typeof cp.marketingAllowed === 'function' ? cp.marketingAllowed() === true : false;
      saleOfData = typeof cp.saleOfDataAllowed === 'function' ? cp.saleOfDataAllowed() === true : true;
    } catch(e) {}

    return {
      analytics_storage: analytics,
      ad_storage: marketing && saleOfData,
      ad_user_data: marketing && saleOfData,
      ad_personalization: marketing && saleOfData,
      state: 'resolved',
      source: 'shopify_customer_privacy'
    };
  };

  // Read only explicit recorded values. Processing permissions do not prove a choice.
  PalasAnalytics.shopifyConsent = function() {
    var value = {}, status = 'unavailable';
    try {
      var cookies = document.cookie.split(';').map(function(c) { return c.trim(); }).filter(function(c) { return c.indexOf('_tracking_consent=') === 0; });
      if (cookies.length === 1 && cookies[0].length <= 4096) {
        var raw = decodeURIComponent(cookies[0].slice('_tracking_consent='.length));
        var stored = JSON.parse(raw);
        if (stored.v === '2.1' && stored.con && stored.con.CMP && !Array.isArray(stored.con.CMP) &&
            ['a', 'm', 'p', 's'].every(function(k) { return stored.con.CMP[k] === undefined || ['', '0', '1'].indexOf(stored.con.CMP[k]) !== -1; })) {
          var fields = { analytics: 'a', marketing: 'm', preferences: 'p', sale_of_data: 's' };
          Object.keys(fields).forEach(function(k) {
            var v = stored.con.CMP[fields[k]];
            value[k] = v === '1' ? 'yes' : v === '0' ? 'no' : '';
          });
          if (Object.keys(value).some(function(k) { return value[k] === 'yes' || value[k] === 'no'; })) status = 'recorded';
        }
      }
    } catch (_) {}
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (cp && typeof cp.currentVisitorConsent === 'function') {
      // Liquid has Shopify's same-origin API; let its SDK read server-set consent from fetch responses.
      cp.backendConsentEnabled = true;
      try {
        // The live API overrides a legacy cookie, including revocations or resets.
        value = cp.currentVisitorConsent() || {};
        status = ['analytics', 'marketing', 'preferences', 'sale_of_data'].some(function(k) {
          return value[k] === 'yes' || value[k] === 'no';
        }) ? 'recorded' : (typeof cp.shouldShowBanner === 'function' && cp.shouldShowBanner() === false ? 'not_required' : 'pending');
      } catch (_) {}
    }
    function decision(v) { return v === 'yes' ? 'granted' : v === 'no' ? 'denied' : 'unknown'; }
    return { consent: { analytics: decision(value.analytics), advertising: value.sale_of_data === 'no' ? 'denied' : decision(value.marketing),
      preferences: decision(value.preferences), sale_of_data: decision(value.sale_of_data) }, options: { source: 'shopify', status: status } };
  };

  // Each occurrence has its own key: one tab cannot overwrite another tab's pending choice.
  var consentOutboxKey = 'palas_signal_consent_outbox';
  var consentFactPrefix = consentOutboxKey + ':';
  var activeConsentChoice = null, signalConsentReady = false, consentOrder = 0;
  function consentGeneration() { return window.localStorage.getItem('_cs_generation') || null; }
  function consentOccurrence(requested) {
    var eventId;
    try { eventId = window.crypto && window.crypto.randomUUID(); } catch (_) {}
    if (!eventId) eventId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      var n = Math.floor(Math.random() * 16); return (c === 'x' ? n : (n & 3) | 8).toString(16);
    });
    var page;
    if (typeof location !== 'undefined' && location.href) page = { url: location.href, title: document.title || '', referrer: document.referrer || '' };
    return { eventId: eventId, at: Date.now(), requested: requested, page: page, generation: consentGeneration(), order: ++consentOrder };
  }
  function consentOutbox() {
    var store = window.localStorage, generation = consentGeneration(), pending = [], last = null;
    var saved = JSON.parse(store.getItem(consentOutboxKey) || 'null');
    if (saved && saved.generation === generation) last = saved.last;
    var keys = [];
    for (var i = 0; i < store.length; i++) { var key = store.key(i); if (key && key.indexOf(consentFactPrefix) === 0) keys.push(key); }
    keys.forEach(function(key) {
      try {
        var fact = JSON.parse(store.getItem(key) || 'null');
        if (!fact || fact.generation !== generation) {
          // A sibling may already have reset and written a new identity fact.
          if (consentGeneration() === generation) store.removeItem(key);
          return;
        }
        if (fact.options && fact.options.eventId && typeof fact.options.at === 'number') pending.push(fact);
      } catch (_) { /* a concurrent acknowledgement may already have removed this occurrence */ }
    });
    pending.sort(function(a,b) { return a.options.at - b.options.at || (a.order || 0) - (b.order || 0) || a.options.eventId.localeCompare(b.options.eventId); });
    return { pending: pending, last: last, generation: generation };
  }
  PalasAnalytics.beginShopifyConsent = function(requested) {
    activeConsentChoice = consentOccurrence(requested);
    return activeConsentChoice;
  };
  PalasAnalytics.flushSignalConsent = function() {
    if (!signalConsentReady || !window.Signal) return;
    var outbox;
    try { outbox = consentOutbox(); } catch (_) { return; }
    if (!outbox.pending.length) return;
    // Replayed facts preserve their original state and never replace today's Shopify baseline.
    var current = PalasAnalytics.shopifyConsent();
    window.Signal.observeConsent(current.consent, current.options);
    outbox.pending.forEach(function(fact) {
      try {
        if (consentGeneration() !== fact.generation) return;
        if (window.Signal.changeConsent(fact.consent, Object.assign({}, fact.options, { historical: true, force: true })) === true)
          {
          window.localStorage.removeItem(consentFactPrefix + fact.options.eventId);
          var meta = JSON.parse(window.localStorage.getItem(consentOutboxKey) || 'null');
          if (meta && meta.generation === fact.generation && meta.last && meta.last.eventId === fact.options.eventId) {
            meta.last.handedOff = true; window.localStorage.setItem(consentOutboxKey, JSON.stringify(meta));
          }
        }
      } catch (_) { /* keep this occurrence for a later handoff */ }
    });
  };
  PalasAnalytics.signalConsentReady = function() {
    signalConsentReady = true;
    PalasAnalytics.flushSignalConsent();
  };
  PalasAnalytics.confirmShopifyConsent = function(occurrence) {
    var choice = PalasAnalytics.shopifyConsent();
    if (choice.options.status !== 'recorded') return false;
    if (!occurrence && activeConsentChoice && activeConsentChoice.requested) {
      var current;
      try { current = window.Shopify.customerPrivacy.currentVisitorConsent(); } catch (_) { return false; }
      if (!Object.keys(activeConsentChoice.requested).every(function(key) {
        return current[key] === (activeConsentChoice.requested[key] ? 'yes' : 'no');
      })) return false;
    }
    try {
      var outbox = consentOutbox(), signature = JSON.stringify(choice.consent);
      occurrence = occurrence || activeConsentChoice || (outbox.last && outbox.last.signature === signature ? outbox.last : null) || consentOccurrence();
      if (occurrence.generation !== outbox.generation) return false;
      if (outbox.last && outbox.last.eventId === occurrence.eventId && outbox.last.handedOff) return true;
      var factKey = consentFactPrefix + occurrence.eventId;
      // Never acknowledge a new fact by evicting another confirmed choice.
      if (outbox.pending.length >= 64 && !window.localStorage.getItem(factKey)) return false;
      var fact = { generation: outbox.generation, order: occurrence.order, consent: choice.consent,
        options: Object.assign({}, choice.options, { eventId: occurrence.eventId, at: occurrence.at, page: occurrence.page }) };
      window.localStorage.setItem(consentFactPrefix + occurrence.eventId, JSON.stringify(fact));
      window.localStorage.setItem(consentOutboxKey, JSON.stringify({ generation: outbox.generation,
        last: { signature: signature, eventId: occurrence.eventId, at: occurrence.at, page: occurrence.page, generation: outbox.generation, order: occurrence.order } }));
    } catch (_) { return false; }
    PalasAnalytics.flushSignalConsent();
    return true;
  };
  PalasAnalytics.finishShopifyConsent = function(occurrence) {
    if (activeConsentChoice === occurrence) activeConsentChoice = null;
  };

  var privacyWatchers = [], privacyTimer = null, privacyAttempts = 0, privacyWatching = false, privacySignature = null, privacyLoadFailed = false;
  var privacyFailureOrigin = 'none', privacyFailureKind = 'none';
  var privacyLoading=false,privacyLoadSequence=0,privacyLoads=0,privacyLoadTimer=null,privacyLoadWaiters=[];
  var privacyFailedScript = '', privacyScriptReloaded = false;
  var privacyBootTimer = null, privacyBootChecks = 0;
  function privacyAsset(error) {
    var message = error && typeof error.message === 'string' ? error.message : '';
    // Shopify returns an array, and caches failed script elements by reference.
    // Only its exact, versioned official asset is eligible for one fresh load.
    var match = /^load error: (https:\/\/cdn\.shopify\.com\/shopifycloud\/consent-tracking-api\/v0\.2\/consent-tracking-api(?:-legacy)?\.js)$/.exec(message);
    return match ? match[1] : '';
  }
  function rememberPrivacyFailure(error, origin) {
    privacyLoadFailed = !!error;
    privacyFailureOrigin = error ? origin : 'none';
    privacyFailedScript = '';
    var errors = Array.isArray(error) ? error.slice(0, 4) : [error];
    var kinds = errors.map(function(item) {
      var asset = privacyAsset(item);
      if (asset) privacyFailedScript = asset;
      var name = item && typeof item.name === 'string' ? item.name : '';
      return asset ? 'network' : ({NetworkError:'network',TimeoutError:'timeout',AbortError:'abort',TypeError:'type_error'})[name] || 'other';
    });
    privacyFailureKind = !error ? 'none' : kinds.length && kinds.every(function(kind) { return kind === kinds[0]; }) ? kinds[0] : 'other';
  }
  function retryPrivacyScript() {
    if (!privacyFailedScript || privacyScriptReloaded || !document.querySelectorAll) return null;
    var scripts = document.querySelectorAll('script[src]');
    for (var i = 0; i < scripts.length; i++) {
      var script = scripts[i];
      if (script.src !== privacyFailedScript || typeof document.createElement !== 'function') continue;
      // Cloning an already-started script also clones its execution flag.
      var retry = document.createElement('script');
      ['type','nomodule','defer','async','crossorigin','integrity','referrerpolicy'].forEach(function(name) {
        if (script.hasAttribute(name)) retry.setAttribute(name, script.getAttribute(name));
      });
      if (script.nonce) retry.nonce = script.nonce;
      // A failed ES module is cached by URL even after its element is removed.
      // Keep Shopify's type/nonce/integrity and request the same official asset once.
      retry.src = privacyFailedScript + '?signal_retry=1';
      privacyScriptReloaded = true;
      return retry;
    }
    return null;
  }
  PalasAnalytics.consentApiDiagnostic = function() {
    return { api_failure_origin: privacyFailureOrigin, api_failure_kind: privacyFailureKind, api_attempts: privacyAttempts,api_loads:privacyLoads,api_inflight:privacyLoading };
  };
  function loadPrivacyApi(callback) {
    if(callback) privacyLoadWaiters.push(callback);
    if(privacyLoading) return;
    if(!window.Shopify || typeof window.Shopify.loadFeatures!=='function') {
      // The supported loader can arrive just after this script. Do not wait for
      // the five-second failure retry slot before its first available request.
      if (privacyLoads === 0 && privacyBootTimer === null && privacyBootChecks < 50) {
        privacyBootTimer = setTimeout(function() {
          privacyBootTimer = null; privacyBootChecks++;
          var cp = window.Shopify && window.Shopify.customerPrivacy;
          if (cp && typeof cp.currentVisitorConsent === 'function') { notifyPrivacy(); return; }
          loadPrivacyApi();
        }, 100);
      }
      var waiting=privacyLoadWaiters;privacyLoadWaiters=[];waiting.forEach(function(fn){fn();});return;
    }
    if (privacyBootTimer !== null) { clearTimeout(privacyBootTimer); privacyBootTimer = null; }
    privacyLoading=true;var sequence=++privacyLoadSequence;privacyLoads++;
    function finish(error,origin) {
      if(!privacyLoading || sequence!==privacyLoadSequence)return;
      privacyLoading=false;clearTimeout(privacyLoadTimer);privacyLoadTimer=null;
      rememberPrivacyFailure(error,origin);
      var waiting=privacyLoadWaiters;privacyLoadWaiters=[];waiting.forEach(function(fn){fn();});
      if(window.Shopify.customerPrivacy && typeof window.Shopify.customerPrivacy.currentVisitorConsent==='function') notifyPrivacy();
    }
    privacyLoadTimer=setTimeout(function(){finish({name:'TimeoutError'},'callback');},8000);
    try {
      var retry = retryPrivacyScript();
      if (retry) {
        retry.addEventListener('load',function(){
          var cp = window.Shopify && window.Shopify.customerPrivacy;
          finish(cp && typeof cp.currentVisitorConsent === 'function' ? null : {name:'TypeError'},'callback');
        },{once:true});
        retry.addEventListener('error',function(){finish({name:'NetworkError'},'callback');},{once:true});
        document.head.appendChild(retry);
      } else window.Shopify.loadFeatures([{name:'consent-tracking-api',version:'0.2'}],function(error){finish(error,'callback');});
    }
    catch(error){finish(error,'throw');}
  }
  function checkPrivacy() {
    privacyTimer = null;
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    var hasApi = !!(cp && typeof cp.currentVisitorConsent === 'function');
    var ready = hasApi;
    var stage = !cp ? 'object_absent' : !hasApi ? 'reader_absent' : '';
    if (ready) { try { cp.currentVisitorConsent(); rememberPrivacyFailure(null, 'none'); } catch (_) { ready = false; stage = 'reader_error'; } }
    if (!ready && privacyLoadFailed && !hasApi) stage = 'loader_error';
    var state = PalasAnalytics.shopifyConsent();
    var signalClock = window.Signal && typeof window.Signal.state==='function' ? window.Signal.state().clockStatus : 'pending';
    var signature = JSON.stringify([ready, state, stage, signalClock]);
    var deliveryFailed = false;
    if (signature !== privacySignature) {
      privacyWatchers.forEach(function(fn) {
        try { if (fn(ready, stage) === false) deliveryFailed = true; } catch (_) { deliveryFailed = true; }
      });
      if (!deliveryFailed) privacySignature = signature;
    }
    var incomplete = ['analytics', 'advertising', 'preferences'].some(function(k) { return state.consent[k] === 'unknown'; });
    if ((deliveryFailed || !ready || incomplete || state.options.status !== 'recorded') && privacyAttempts < 60) {
      if (!hasApi && (privacyLoads === 0 || [0, 5, 15, 30].indexOf(privacyAttempts) !== -1) && window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
        loadPrivacyApi();
      }
      privacyAttempts++;
      privacyTimer = setTimeout(checkPrivacy, 1000);
    } else if (!ready || deliveryFailed) {
      privacyTimer = setTimeout(function() { privacyAttempts = 0; checkPrivacy(); }, 60000);
    }
  }
  function notifyPrivacy() {
    if (privacyTimer !== null) clearTimeout(privacyTimer);
    privacyAttempts = 0;
    privacySignature = null;
    checkPrivacy();
  }
  PalasAnalytics.retryConsentApi = notifyPrivacy;
  PalasAnalytics.watchConsentApi = function(callback) {
    privacyWatchers.push(callback);
    if (!privacyWatching) {
      privacyWatching = true;
      document.addEventListener('consentTrackingApiLoaded', notifyPrivacy);
      document.addEventListener('signalClockReady', notifyPrivacy);
      document.addEventListener('visitorConsentCollected', notifyPrivacy);
      if (window.addEventListener) {
        window.addEventListener('pageshow', notifyPrivacy);
        window.addEventListener('online', notifyPrivacy);
        window.addEventListener('focus', notifyPrivacy);
      }
      notifyPrivacy();
    } else notifyPrivacy();
  };

  PalasAnalytics.withConsentApi = function(callback) {
    if (PalasAnalytics.shopifyConsent().options.status === 'recorded' || (window.Shopify && window.Shopify.customerPrivacy && typeof window.Shopify.customerPrivacy.currentVisitorConsent === 'function')) { callback(); return; }
    var called = false;
    var timer = setTimeout(done, 2000);
    function done() {
      if (called) return;
      called = true;
      clearTimeout(timer);
      callback();
    }
    loadPrivacyApi(done);
  };

  PalasAnalytics.postEventHub = function(payload) {
    if (!payload || !window.fetch) return;
    payload = PalasAnalytics.withMuidProperties(payload);
    try {
      fetch(PalasAnalytics.eventHubEndpoint + '/ingest', {
        method: 'POST',
        credentials: 'include',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).catch(function() {});
    } catch(e) {}
  };

  PalasAnalytics.ensureMuid = function() {
    var seededMuid = PalasAnalytics.getMuid({ create: true });
    if (!window.fetch) return Promise.resolve(seededMuid);
    if (PalasAnalytics._muidPromise) return PalasAnalytics._muidPromise;
    try {
      var endpoint = PalasAnalytics.eventHubEndpoint + '/muid';
      try {
        var token = new URLSearchParams(window.location.search).get('u');
        if (token) endpoint += '?u=' + encodeURIComponent(token);
        else if (seededMuid) endpoint += '?m=' + encodeURIComponent(seededMuid);
      } catch(e) {}
      PalasAnalytics._muidPromise = fetch(endpoint, {
        method: 'GET',
        credentials: 'include',
        keepalive: true
      })
        .then(function(response) { return response && response.ok ? response.json() : null; })
        .then(function() { return seededMuid; })
        .catch(function() { return seededMuid; });
      return PalasAnalytics._muidPromise;
    } catch(e) {
      return Promise.resolve(seededMuid);
    }
  };

  /**
   * Diff two CartPayloads and return items whose quantity changed.
   * @param {CartPayload|null} prev
   * @param {CartPayload|null} curr
   * @returns {Array<CartItem & {quantity_change: number}>}
   */
  PalasAnalytics.diffItems = function(prev, curr) {
    if (!prev || !curr) return [];
    var prevMap = {};
    prev.items.forEach(function(item) { prevMap[item.id] = item.quantity; });
    var currMap = {};
    curr.items.forEach(function(item) { currMap[item.id] = item.quantity; });

    var changed = [];
    curr.items.forEach(function(item) {
      var prevQty = prevMap[item.id] || 0;
      if (item.quantity > prevQty) {
        changed.push(Object.assign({}, item, { quantity_change: item.quantity - prevQty }));
      }
    });
    prev.items.forEach(function(item) {
      var currQty = currMap[item.id] || 0;
      if (currQty < item.quantity) {
        changed.push(Object.assign({}, item, { quantity_change: currQty - item.quantity }));
      }
    });
    return changed;
  };

  /**
   * Capture an analytics event on PostHog (canonical shape) and optionally
   * Klaviyo (flat cart payload, commerce convention).
   *
   * @param {string} eventName             PostHog event name, e.g. 'cart:updated'.
   * @param {AnalyticsEvent} props         Event properties (canonical shape).
   * @param {string} [klaviyoEventName]    If provided and `props.cart` exists,
   *                                       sends a flat payload to Klaviyo under this name.
   */
  PalasAnalytics.capture = function(eventName, props, klaviyoEventName) {
    props = props || {};
    if (!props.event_id) props.event_id = PalasAnalytics.eventId(eventName);
    PalasAnalytics.withTrackingProperties(props);
    PalasAnalytics.withConsentProperties(props);
    PalasAnalytics.withMuidProperties(props);
    PalasAnalytics.withBrowserIdentifiers(props);
    if (typeof window.posthog !== 'undefined') {
      window.posthog.capture(eventName, props);
    }
    if (klaviyoEventName && typeof window.klaviyo !== 'undefined' && props.cart) {
      var cart = props.cart;
      var klPayload = Object.assign({}, cart, {
        cart_token: cart.token,
        $currency_code: cart.currency,
        currency_code: cart.currency
      });
      window.klaviyo.track(klaviyoEventName, klPayload);
    }
  };

  // Independent evidence when the Signal queue itself cannot deliver.
  if (document.addEventListener) document.addEventListener('signalDiagnostic',function(event) {
    var d=event.detail||{},props={},identity={};
    if (!/^(identity|delivery|clock_start|clock_result|events_start|events_retry|exit|network_pending|network_headers|network_resource|network_policy|lifecycle|previous_delivery)$/.test(d.stage||'')) return;
    ['stage','transport','kind','outcome','request_id','event_count','event_ids','visitor_id','session_id','receipt_visitor_id','sdk_document_id','identity_sequence','clock_status','proved','status','accepted','rejected','elapsed_ms','rtt_ms','queued','diagnostic_sequence','sdk_version','visibility','body_bytes','error_name','endpoint_host','ready_state','online','service_worker','prerendering','was_discarded','document_ms','effective_type','navigation_type','network_phase','attempt','keepalive','pending','lifecycle','persisted','previous_lifecycle','previous_outcome','previous_visibility','previous_probe_id','previous_sdk_document_id','previous_sdk_version','resource_ms','resource_match','timing_exposed','transfer_bytes','resource_status','next_hop','policy_disposition'].forEach(function(key) {
      var value=d[key];
      if (typeof value==='string' && value.length<=128 || typeof value==='number' && isFinite(value) || typeof value==='boolean' || value===null) props['palas_signal_'+key]=value;
      if (key==='event_ids' && Array.isArray(value)) props.palas_signal_event_ids=value.filter(function(v){return typeof v==='string'&&/^[a-f0-9-]{36}$/i.test(v);}).slice(0,50);
    });
    var reasons=d.rejection_reason_counts,counts={},total=0;
    if (d.stage==='delivery' && d.kind==='events' && reasons && typeof reasons==='object' && !Array.isArray(reasons)) {
      ['invalid_event','duplicate_event','mixed_visitor','unknown_event_name','purchase_not_allowed','invalid_user_id','other'].forEach(function(reason) {
        var count=reasons[reason];
        if (typeof count==='number' && isFinite(count) && Math.floor(count)===count && count>0 && count<=50) { counts[reason]=count;total+=count; }
      });
      if (total>0 && total<=50) props.palas_signal_rejection_reason_counts=counts;
    }
    if (typeof d.probe_id==='string'&&d.probe_id.length<=128) props.palas_signal_document_id=d.probe_id;
    if (d.stage==='identity') {
      ['visitor_id','session_id','sdk_document_id','clock_status','proved'].forEach(function(key){if(props['palas_signal_'+key]!==undefined)identity['palas_signal_'+key]=props['palas_signal_'+key];});
      PalasAnalytics.signalIdentity=identity;
    }
    try { if (window.posthog&&typeof window.posthog.capture==='function') window.posthog.capture('palas:signal_diagnostic',PalasAnalytics.withConsentProperties(props), /^(network_|lifecycle$|previous_delivery$|events_retry$|exit$)/.test(d.stage||'') || d.stage==='delivery'&&d.kind==='events' ? {send_instantly:true} : undefined); } catch (_) {}
  });
  window.PalasAnalytics = PalasAnalytics;
  PalasAnalytics.withConsentApi(function() {
    PalasAnalytics.ensureMuid();
  });
})(window);
